import {Dispatch} from "@reduxjs/toolkit"
import {connect} from "react-redux"
import {createSelector} from "reselect"
import {setFacet, toggleViewMoreAction} from "../../ducks/search"
import {setViewAllOpen} from "../../ducks/viewAllModal"
import {
    FILTERS_SHOW_VIEW_ALL_COUNT,
    ViewLinkOptions,
    FILTERS_SHOW_VIEW_MORE_COUNT,
    FILTERS_SHOW_VIEW_MORE_COUNT_EXTENDED,
} from "../../config/constants"
import State from "../../models/State"
import {FilterFacet} from "../../models/Filter"
import TrackFilterViewAll from "../../events/trackEvent/events/trackFilterViewAll"
import {FacetsState} from "../../models/FacetsState"
import {TextModel} from "../../models/Text"

type StateProps = {
    viewLink: ViewLinkOptions
    firstFacets: string[]
    lastFacets: string[]
    isViewMoreOpen: boolean
    facets: FacetsState
    isFetchingPageItems: boolean
    text: TextModel
}

export const isAnySelectedFacetOutOfView = (facets, facetsObj) => {
    if (facetsObj) {
        const slicedFacets = facets.slice(FILTERS_SHOW_VIEW_MORE_COUNT)
        const onlySelectedValues = (facet: string) => facetsObj[facet]?.s === true
        const arrayOfWorkableSelections = slicedFacets.filter(onlySelectedValues)
        return arrayOfWorkableSelections.length > 0
    }
    return false
}

const selectFacets = (state: State) => state.search.facets
const selectFilters = (state: State, facetName: string) => state.search.filters[facetName]

export const makeSelectFacets = createSelector([selectFacets, selectFilters], (facetsObj, filterFacet) => {
    const {facets} = filterFacet as FilterFacet

    let viewLink: ViewLinkOptions
    switch (true) {
        case facets.length > FILTERS_SHOW_VIEW_ALL_COUNT:
            viewLink = ViewLinkOptions.viewAll
            break
        case facets.length > FILTERS_SHOW_VIEW_MORE_COUNT:
            viewLink = ViewLinkOptions.viewMore
            break
        default:
            viewLink = ViewLinkOptions.none
    }

    const isViewAll = viewLink === ViewLinkOptions.viewAll

    const firstFacets = facets.slice(
        0,
        isViewAll && isAnySelectedFacetOutOfView(facets, facetsObj)
            ? FILTERS_SHOW_VIEW_MORE_COUNT_EXTENDED
            : FILTERS_SHOW_VIEW_MORE_COUNT,
    )
    const lastFacets = facets.slice(FILTERS_SHOW_VIEW_MORE_COUNT, FILTERS_SHOW_VIEW_ALL_COUNT)

    return {
        firstFacets,
        lastFacets,
        viewLink,
    }
})

export const mapStateToProps = (state: State, ownProps: any) => {
    const {facets, isViewMoreOpen} = state.search.filters[ownProps.facetName] as FilterFacet
    const {facets: facetsObj, isFetchingPageItems} = state.search
    const {firstItemRef, facetName, filterName} = ownProps

    if (!facets) return {}

    const {text} = state

    return {
        filterName,
        facetName,
        firstItemRef,
        isViewMoreOpen,
        facets: facetsObj,
        isFetchingPageItems,
        text,
        ...makeSelectFacets(state, facetName),
    } as StateProps
}

export const mapDispatchToProps = (dispatch: any) => ({
    toggleViewMore: ({facetName, filterName}: {facetName: string; filterName: string}) =>
        dispatch(toggleViewMoreAction(facetName, filterName)) as Dispatch,
    openViewAllModal: ({facetName, filterName}: {facetName: string; filterName: string}) => {
        dispatch(setViewAllOpen(facetName))
        TrackFilterViewAll(filterName)
    },
    handleSetFacet: (facetValue: string) => {
        dispatch(setFacet(facetValue))
    },
})

export default connect(mapStateToProps, mapDispatchToProps)
